<script lang="ts">
import type { Image } from '~/utils/crystallize/component-schemas'
import type { ClassValue } from '~/utils/types'

export interface CrystallizeImageProps {
  image?: Image | null
  sizes?: string
  imgClass?: ClassValue
  hideCaption?: boolean
}
</script>

<script setup lang="ts">
const props = defineProps<CrystallizeImageProps>()

const sources = computed(() => {
  const srcsetGrouping = props.image?.variants?.reduce<Record<string, { priority: number, srcset: string[] }>>((acc, variant) => {
    const extAndPriority = getExtAndPriority(variant)
    if (extAndPriority) {
      const { ext, priority } = extAndPriority
      const type = `image/${ext === 'jpg' ? 'jpeg' : ext}`
      acc[type] ||= { priority, srcset: [] }
      acc[type].srcset.push(`${variant.url} ${variant.width}w`)
    }
    return acc
  }, {})

  if (props.image?.url?.endsWith('.svg') && srcsetGrouping) {
    // If the image is an SVG, we add the raw url to the srcset with max priority, so that, if the browser supports it, we render the raw SVG.
    // If the browser does not support SVGs in img tags, it will fall through to the next source.
    srcsetGrouping['image/svg+xml'] = { priority: -1, srcset: [props.image.url] }
  }

  return Object.entries(srcsetGrouping ?? {}).sort(([, a], [, b]) => a.priority - b.priority)
})

const meta = computed<Record<Lowercase<string>, string>>(() => {
  const valid = (props.image?.meta ?? [])
    .map(({ key, value }) => [key?.toLowerCase(), value])
    .filter(([key, value]) => !!key && !!value) as [Lowercase<string>, string][]

  return Object.fromEntries(valid)
})
</script>

<template>
  <figure v-if="image && !hideCaption && image?.caption?.json?.length">
    <picture v-if="sources.length > 0">
      <source
        v-for="([type, { srcset }]) in sources"
        :key="type"
        :type="type"
        :srcset="srcset.join(', ')"
        :sizes="sizes"
      >
      <img
        :src="image.url"
        :alt="image.altText ?? ''"
        :sizes="sizes"
        :class="imgClass"
      >
    </picture>
    <img
      v-else
      :src="image.url"
      :alt="image.altText ?? ''"
      :sizes="sizes"
      :class="imgClass"
    >
    <figcaption>
      <CrystallizeRichText
        v-for="(block, i) in image.caption.json"
        :key="i"
        :block="block"
        class="text-base"
      />

      <slot name="meta" :meta="meta" />
    </figcaption>

    <slot />
  </figure>

  <picture v-else-if="image">
    <source
      v-for="([type, { srcset }]) in sources"
      :key="type"
      :type="type"
      :srcset="srcset.join(', ')"
      :sizes="sizes"
      class="absolute"
    >
    <img
      :src="image.url"
      :alt="image.altText ?? ''"
      :sizes="sizes"
      :class="imgClass"
    >
    <slot />
  </picture>
</template>
